import { Authenticator } from '@aws-amplify/ui-react'
import { fetchAuthSession } from 'aws-amplify/auth'
import React, { useEffect, useState } from 'react'
import { authTokenCache } from '../../lib/auth-token-cache'

type AuthenticatorProps = React.ComponentProps<typeof Authenticator>

export interface SessionAwareAuthenticatorProps extends Omit<AuthenticatorProps, 'children'> {
  children: AuthenticatorProps['children']
}

export const SessionAwareAuthenticator: React.FC<SessionAwareAuthenticatorProps> = ({
  children,
  ...authenticatorProps
}) => {
  const [isCheckingSession, setIsCheckingSession] = useState(true)
  const [hasExistingSession, setHasExistingSession] = useState(false)
  const [componentKey, setComponentKey] = useState('initial')
  const [hasRechecked, setHasRechecked] = useState(false)

  useEffect(() => {
    const checkExistingSession = async (forcedByEvent = false): Promise<void> => {
      try {
        console.info('🔍 [SESSION_AWARE] Checking for existing session...')

        try {
          const cachedToken = await authTokenCache.getToken()
          if (cachedToken) {
            console.info('✅ [SESSION_AWARE] Found cached token - session exists')

            if (!hasExistingSession || forcedByEvent) {
              if (!hasExistingSession) {
                console.info(
                  '🔄 [SESSION_AWARE] Session state changed: false -> true, updating key'
                )
              } else {
                console.info('🔄 [SESSION_AWARE] Cross-tab event - ensuring authenticated state')
              }
              setHasExistingSession(true)
              if (!forcedByEvent) {
                setComponentKey(`authenticated-${Date.now()}`)
              }
            }
            setIsCheckingSession(false)
            return
          }
        } catch (error) {
          console.info('ℹ️ [SESSION_AWARE] Error getting cached token:', error)
        }

        const session = await fetchAuthSession()
        const accessToken = session.tokens?.accessToken?.toString()

        if (accessToken) {
          console.info('✅ [SESSION_AWARE] Found AWS session - session exists')

          if (!hasExistingSession || forcedByEvent) {
            if (!hasExistingSession) {
              console.info('🔄 [SESSION_AWARE] Session state changed: false -> true, updating key')
            } else {
              console.info('🔄 [SESSION_AWARE] Cross-tab event - ensuring authenticated state')
            }
            setHasExistingSession(true)
            if (!forcedByEvent) {
              setComponentKey(`authenticated-${Date.now()}`)
            }
          }
        } else {
          console.info('ℹ️ [SESSION_AWARE] No existing session found')

          if (hasExistingSession) {
            console.info('🔄 [SESSION_AWARE] Session state changed: true -> false, updating key')
            setHasExistingSession(false)
            setComponentKey(`unauthenticated-${Date.now()}`)
          }
        }
      } catch (error) {
        console.info('ℹ️ [SESSION_AWARE] No session found:', error)

        if (hasExistingSession) {
          console.info('🔄 [SESSION_AWARE] Session state changed: true -> false, updating key')
          setHasExistingSession(false)
          setComponentKey(`unauthenticated-${Date.now()}`)
        }
      } finally {
        setIsCheckingSession(false)
      }
    }

    void checkExistingSession()

    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === 'payments_auth_state' && e.newValue && !hasRechecked) {
        console.info('🔄 [SESSION_AWARE] Cross-tab auth detected - rechecking session (one time)')
        setHasRechecked(true)

        const newKey = `crossTab-${Date.now()}`
        console.info(`🔄 [SESSION_AWARE] Forcing key update for cross-tab event: ${newKey}`)
        setComponentKey(newKey)

        void checkExistingSession(true)
      }

      if (e.key === 'payments_logout_signal' && e.newValue) {
        console.info(
          '🚨 [SESSION_AWARE] Cross-tab logout detected - resetting state for next login cycle'
        )
        setHasRechecked(false)
        setHasExistingSession(false)
        setComponentKey(`logout-${Date.now()}`)
        setIsCheckingSession(false)
      }

      if (e.key === 'token' && !e.newValue && hasExistingSession) {
        console.info('🚨 [SESSION_AWARE] Token removed - resetting state for next login cycle')
        setHasRechecked(false)
        setHasExistingSession(false)
        setComponentKey(`tokenRemoved-${Date.now()}`)
        setIsCheckingSession(false)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [hasExistingSession, hasRechecked])

  if (isCheckingSession) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-gray-50'>
        <div className='text-center max-w-md mx-auto p-8'>
          <div className='animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6'></div>
          <h2 className='text-xl font-semibold text-gray-900 mb-2'>Checking Authentication</h2>
          <p className='text-gray-600'>Please wait while we verify your session...</p>
        </div>
      </div>
    )
  }

  if (hasExistingSession) {
    console.info(
      '🎯 [SESSION_AWARE] Using existing session - setting initialState to authenticated'
    )
    console.info(`🔑 [SESSION_AWARE] Using component key: ${componentKey}`)
    return (
      // @ts-expect-error AWS Amplify supports 'authenticated' but TypeScript types are incomplete
      <Authenticator key={componentKey} {...authenticatorProps} initialState='authenticated'>
        {children}
      </Authenticator>
    )
  }

  console.info('🔑 [SESSION_AWARE] No session - showing login form')
  console.info(`🔑 [SESSION_AWARE] Using component key: ${componentKey}`)
  return (
    <Authenticator key={componentKey} {...authenticatorProps}>
      {children}
    </Authenticator>
  )
}
